import { DateTime } from "luxon";

import {
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
} from "../models/ProductionDashboard";
import { assemblyElapsedSeconds } from "./assemblyElapsed";
import { isAwaitingNextStation } from "./awaitingNextStation";
import { cycleTimeBand } from "./cycleTimeBand";

/**
 * What a vehicle on a bay is doing right now, as the Bays view colours it.
 *
 * - over / warning / working: the assembly timer is running, banded against the assembly's
 *   target and warning times (see cycleTimeBand).
 * - waiting: every part on the current assembly is actioned and the timer is stopped - finished
 *   here, nobody has moved it on yet.
 * - paused: timer stopped part-way through an assembly that has had some time on it.
 * - notStarted: on the bay with no time recorded against the current assembly yet.
 */
export type BayVehicleState = "over" | "warning" | "waiting" | "paused" | "working" | "notStarted";

export type BayCardState = BayVehicleState | "idle";

// Worst first: a bay holding several vehicles takes the colour of the one most in need of a look.
const STATE_PRIORITY: BayVehicleState[] = [
  "over",
  "warning",
  "waiting",
  "paused",
  "working",
  "notStarted",
];

export function vehicleState(vehicle: IProductionDashboardBayVehicle, now: DateTime): BayVehicleState {
  if (isAwaitingNextStation(vehicle)) return "waiting";

  if (vehicle.currentAssemblyStartedOn) {
    const band = cycleTimeBand(
      assemblyElapsedSeconds(vehicle, now),
      vehicle.currentAssemblyWarningMinutes,
      vehicle.currentAssemblyAverageMinutes
    );
    if (band === "over") return "over";
    if (band === "warning") return "warning";
    return "working";
  }

  return (vehicle.currentAssemblyElapsedSeconds ?? 0) > 0 ? "paused" : "notStarted";
}

export function bayState(vehicles: IProductionDashboardBayVehicle[], now: DateTime): BayCardState {
  if (vehicles.length === 0) return "idle";

  const states = vehicles.map((v) => vehicleState(v, now));
  return STATE_PRIORITY.find((s) => states.includes(s)) ?? "notStarted";
}

/**
 * The vehicles a bay card shows: everything on the bay except vehicles busy in triage or
 * depollution (they have their own panels) and vehicles that have finished every assembly (they
 * sit in Completed Today). Same rule the assembly-flow lanes apply.
 */
export function bayInProgressVehicles(
  bay: IProductionDashboardBay,
  busyVehicleIds?: Set<string>
): IProductionDashboardBayVehicle[] {
  return (bay.currentVehicles ?? []).filter(
    (v) => !busyVehicleIds?.has(v.id) && v.percentComplete < 100
  );
}

export type BayFilter = "all" | "attention" | "running" | "idle";

export const BAY_FILTERS: BayFilter[] = ["all", "attention", "running", "idle"];

const ATTENTION_STATES: BayCardState[] = ["over", "warning", "waiting", "paused"];
const RUNNING_STATES: BayCardState[] = ["over", "warning", "working"];

export function bayMatchesFilter(state: BayCardState, filter: BayFilter): boolean {
  switch (filter) {
    case "attention":
      return ATTENTION_STATES.includes(state);
    case "running":
      return RUNNING_STATES.includes(state);
    case "idle":
      return state === "idle";
    default:
      return true;
  }
}
